/**
 * useEntityDetection Hook
 * Debounced AI-powered entity detection for document content
 * Now uses project-scoped backend storage via React Query hooks
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSessionStore } from '@/stores/sessionStore'
import { artifactsApi } from '@/lib/api/client'
import { analyzeContent } from '@/lib/interviewer/analyzer'
import type { DetectionStatus, DetectedEntity, EntityStatus } from '@/types/document'
import type { DocContent } from '@ispot/shared'

interface UseEntityDetectionOptions {
  documentId: string | null
  content: string
  enabled?: boolean
  debounceMs?: number
  minContentLength?: number
}

interface UseEntityDetectionResult {
  status: DetectionStatus
  isAnalyzing: boolean
  detectedCount: number
  error?: string
  triggerAnalysis: () => void
}

// Generate unique entity ID
function generateEntityId(): string {
  return `entity-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

export function useEntityDetection({
  documentId,
  content,
  enabled = true,
  debounceMs = 3000,  // 3 seconds after last keystroke
  minContentLength = 100,
}: UseEntityDetectionOptions): UseEntityDetectionResult {
  const queryClient = useQueryClient()
  const { accountId, projectId, isReady } = useSessionStore()

  // Local state for tracking detection status
  const [status, setStatus] = useState<DetectionStatus>('idle')
  const [detectedCount, setDetectedCount] = useState(0)
  const [error, setError] = useState<string | undefined>()

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastAnalyzedContentRef = useRef<string>('')

  // Mutation for analyzing document
  const analyzeMutation = useMutation({
    mutationFn: async () => {
      if (!accountId || !projectId || !documentId) {
        throw new Error('Missing context')
      }

      // Step 1: Call the unified analyzer (uses backend when available, falls back to frontend)
      const result = await analyzeContent({
        sourceId: documentId,
        sourceType: 'document',
        content: content,
        context: 'Smart document with entity detection',
        projectId,
        accountId,
      })

      // Step 2: Convert facets to detected entities
      const detectedEntities: DetectedEntity[] = result.facets.map((facet) => ({
        id: generateEntityId(),
        type: facet.type,
        content: facet.content,
        evidence: facet.evidence,
        confidence: facet.confidence,
        suggestedOntologyType: facet.suggestedOntologyType,
        suggestedOntologyReason: facet.suggestedOntologyReason,
        status: 'pending' as EntityStatus,
      }))

      // Step 3: Get current artifact to merge content
      const { artifact } = await artifactsApi.get(accountId, projectId, documentId)
      const currentContent = artifact.content as DocContent

      // Step 4: Update artifact with detected entities
      const updatedContent: DocContent = {
        ...currentContent,
        detectedEntities: detectedEntities.map(e => ({
          id: e.id,
          type: e.type,
          content: e.content,
          evidence: e.evidence,
          confidence: e.confidence,
          status: e.status,
          suggestedOntologyType: e.suggestedOntologyType,
        })),
      }

      await artifactsApi.update(accountId, projectId, documentId, {
        content: updatedContent as unknown as Record<string, unknown>,
      })

      return { detectedEntities }
    },
    onMutate: () => {
      setStatus('analyzing')
      setError(undefined)
    },
    onSuccess: (data) => {
      setStatus('complete')
      setDetectedCount(data.detectedEntities.length)
      // Invalidate the document query to refetch with new entities
      queryClient.invalidateQueries({ queryKey: ['smartDoc', accountId, projectId, documentId] })
      queryClient.invalidateQueries({ queryKey: ['smartDocs', accountId, projectId] })
    },
    onError: (err) => {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Analysis failed')
    },
  })

  // Check if analysis should be enabled
  const shouldAnalyze = enabled && isReady && !!documentId

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Debounced analysis
  useEffect(() => {
    if (!shouldAnalyze) return
    if (content.length < minContentLength) return

    // Skip if content hasn't changed
    if (content === lastAnalyzedContentRef.current) return

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(async () => {
      lastAnalyzedContentRef.current = content
      analyzeMutation.mutate()
    }, debounceMs)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [content, shouldAnalyze, debounceMs, minContentLength])

  // Manual trigger function
  const triggerAnalysis = useCallback(() => {
    if (!shouldAnalyze || content.length < minContentLength) return
    lastAnalyzedContentRef.current = content
    analyzeMutation.mutate()
  }, [shouldAnalyze, content, minContentLength, analyzeMutation])

  return {
    status,
    isAnalyzing: analyzeMutation.isPending,
    detectedCount,
    error,
    triggerAnalysis,
  }
}
